import { FC, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Box, Typography, Collapse } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';

import { useExpansion } from '../../../../../hooks/Expansion';
import ApiService from '../../../../../ApiService';
import { CardContent, CardHeader } from './Card';
import CollapsibleCard, { CollapsibleCardProps } from '../../../../../components/Card/CollapsibleCard/CollapsibleCard';
import CollapsibleCardExpandArea from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandArea';
import CollapsibleCardExpandButton from '../../../../../components/Card/CollapsibleCard/CollapsibleCardExpandButton';
import Column from '../../../../../components/Column';
import ExpandButton from '../../../../../components/Button/ExpandButton';
import Row from '../../../../../components/Row';

import { CampaignAdGroup } from '../../../../../types/Campaign';
import { DspAudience as DspAudienceSource } from '../../../../../types/DspAudience';
import CampaignAdGroupAudienceTargetingDataGrid from '../DataGrid/CampaignAdGroupAudienceTargetingDataGrid';
import CampaignAdGroupAudienceTargetingDataGridFilter from '../DataGrid/CampaignAdGroupAudienceTargetingDataGridFilter';
import CampaignAdGroupAudienceTargetingSummaryText from '../Text/CampaignAdGroupAudienceTargetingSummaryText';
import CampaignAdGroupAudienceTargetingChips from '../Chip/CampaignAdGroupAudienceTargetingChips';
import CampaignAdGroupAudienceSourceUtils from '../../../../../utils/CampaignAdGroupAudienceSourceUtils';

interface CampaignAdGroupAudienceTargetingCardProps {
    campaignAdGroup: CampaignAdGroup;
    slotProps?: {
        collapsibleCard?: Partial<CollapsibleCardProps>;
    };
}

const CampaignAdGroupAudienceTargetingCard: FC<CampaignAdGroupAudienceTargetingCardProps> = ({
    campaignAdGroup,
    slotProps = {},
}) => {
    const { expanded, toggleExpanded } = useExpansion();

    const [targets, setTargets] = useState<DspAudienceSource[]>([]);

    const targetIds: string[] = CampaignAdGroupAudienceSourceUtils.toDspAudienceSourceIds(
        campaignAdGroup?.adGroupAudienceSources || []
    );

    const { data, isError, isLoading } = useQuery<DspAudienceSource[]>({
        enabled: !!targetIds.length,
        refetchOnWindowFocus: false,
        queryKey: ['campaign/adGroup/dspAudienceSources', campaignAdGroup.id, targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspAudienceSources({
                ids: targetIds,
            });

            return data;
        },
    });

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: 0,
        pageSize: 5,
    });

    useEffect(() => {
        setTargets(data || []);
    }, [data]);

    useEffect(() => {
        setPaginationModel((model: GridPaginationModel) => ({ ...model, page: 0 }));
    }, [targets]);

    return (
        <CollapsibleCard defaultExpanded {...slotProps?.collapsibleCard}>
            <CardHeader
                title={
                    <Row alignItems="center" gap={1}>
                        <Typography variant="body1">Audience</Typography>
                    </Row>
                }
                action={<CollapsibleCardExpandButton />}
            />
            <CollapsibleCardExpandArea>
                <CardContent>
                    <Column gap={3}>
                        <Column gap={2}>
                            <CampaignAdGroupAudienceTargetingSummaryText items={data || []} />

                            <Row gap={1}>
                                <CampaignAdGroupAudienceTargetingChips items={data || []} />
                            </Row>
                        </Column>

                        <Column>
                            <Box>
                                <ExpandButton
                                    variant="outlined"
                                    size="large"
                                    expand={expanded}
                                    onClick={toggleExpanded}
                                >
                                    {expanded ? 'Hide All Selections' : 'See All Selections'}
                                </ExpandButton>
                            </Box>

                            <Collapse in={expanded} timeout="auto" unmountOnExit>
                                {isError ? (
                                    <Alert severity="error">Failed to load audience targeting.</Alert>
                                ) : (
                                    <Column gap={2}>
                                        <CampaignAdGroupAudienceTargetingDataGridFilter
                                            rows={data || []}
                                            onFilterChange={(filteredRows: DspAudienceSource[]): void => {
                                                setTargets(filteredRows);
                                            }}
                                        />

                                        <CampaignAdGroupAudienceTargetingDataGrid
                                            rows={targets}
                                            loading={isLoading}
                                            paginationModel={paginationModel}
                                            onPaginationModelChange={(model: GridPaginationModel) =>
                                                setPaginationModel(model)
                                            }
                                        />
                                    </Column>
                                )}
                            </Collapse>
                        </Column>
                    </Column>
                </CardContent>
            </CollapsibleCardExpandArea>
        </CollapsibleCard>
    );
};

export default CampaignAdGroupAudienceTargetingCard;
